import Phaser from 'phaser';
import { getSkin } from './data/skins';
import { SaveManager } from './SaveManager';
import { getCosmetic } from './data/cosmetics';
import { AthleteRig } from './AthleteRig';
import { BipedRig } from './BipedRig';
import { MascotSprite } from './MascotSprite';

/** Cosmetics equipped on this athlete (empty for AI/anonymous views without an id). */
function equippedFor(athlete: { id?: string }): Record<string, string> {
  return athlete.id ? SaveManager.instance.equippedFor(athlete.id) : {};
}

/**
 * The baked full-sprite variant key for an equipped head cosmetic (`<skin>_<accessory>`), but only
 * if that texture is actually loaded. Returns null when nothing is equipped or the art isn't present
 * — callers then fall back to the base sprite. Variants are lazy-loaded (see Preload / Cosmetics).
 */
function headVariantKey(scene: Phaser.Scene, skinId: string, equipped: Record<string, string>): string | null {
  const cos = equipped.head ? getCosmetic(equipped.head) : undefined;
  if (!cos?.accessory) return null;
  const key = `${skinId}_${cos.accessory}`;
  return scene.textures.exists(key) ? key : null;
}

/** Animation pose shared by every athlete renderer. */
export type Pose = 'idle' | 'run' | 'celebrate' | 'exhausted';

/**
 * Common interface for anything that draws & animates an athlete. Two implementations:
 * - MascotSprite: whole-body Higgsfield sprite, animated with squash/stretch/bob/lean.
 * - AthleteRig: procedural cutout rig (placeholder skins without art).
 * Scenes program against this, so swapping the art approach never touches gameplay.
 */
export interface AthleteView {
  readonly container: Phaser.GameObjects.Container;
  setMotion(speedNorm: number): void;
  setPose(pose: Pose): void;
  setFacing(facing: number): void;
  update(dt: number): void;
  destroy(): void;
}

/**
 * Picks the renderer by capability, best first:
 * 1. articulated BipedRig (all parts loaded) — real run cycle,
 * 2. whole-body MascotSprite (sprite loaded) — bouncy upright,
 * 3. procedural AthleteRig — placeholder fallback.
 */
export function createAthleteView(
  scene: Phaser.Scene,
  x: number,
  y: number,
  athlete: { skin: string; id?: string },
  displayHeight?: number,
): AthleteView {
  const skin = getSkin(athlete.skin);
  const equipped = equippedFor(athlete);
  // A baked head-accessory variant replaces the WHOLE sprite (even for rigged athletes), so the
  // accessory shows IN-RACE. If the variant isn't preloaded, render the base now and hot-swap the
  // texture as soon as it loads — so the accessory always appears, regardless of preload timing.
  const cos = equipped.head ? getCosmetic(equipped.head) : undefined;
  if (cos?.accessory) {
    const key = `${skin.id}_${cos.accessory}`;
    const dh = displayHeight ?? skin.sprite?.displayHeight ?? skin.rig?.displayHeight ?? 220;
    if (scene.textures.exists(key)) {
      return new MascotSprite(scene, x, y, key, dh, { skinId: skin.id, equipped });
    }
    const baseKey = scene.textures.exists(`${skin.id}_full`) ? `${skin.id}_full` : (skin.sprite?.key ?? `${skin.id}_full`);
    if (scene.textures.exists(baseKey)) {
      const view = new MascotSprite(scene, x, y, baseKey, dh, { skinId: skin.id, equipped });
      scene.load.image(key, `assets/sprites/full/${key}.png`);
      scene.load.once(Phaser.Loader.Events.COMPLETE, () => view.swapBody(key));
      scene.load.start();
      return view;
    }
  }
  if (skin.rig) {
    const p = skin.rig.parts;
    // Tail is optional; the other four parts must be loaded to use the articulated rig.
    const ready = [p.head, p.torso, p.arm, p.leg].every((k) => scene.textures.exists(k));
    if (ready) return new BipedRig(scene, x, y, p, displayHeight ?? skin.rig.displayHeight, skin.rig.geometry);
  }
  if (skin.sprite && scene.textures.exists(skin.sprite.key)) {
    return new MascotSprite(scene, x, y, skin.sprite.key, displayHeight ?? skin.sprite.displayHeight, { skinId: skin.id, equipped });
  }
  return new AthleteRig(scene, x, y, athlete.skin);
}

/**
 * Portrait view for static screens (menu / select / results): the whole-body Higgsfield
 * hero sprite — perfectly composed, no rig assembly. Falls back to the rig if missing.
 */
export function createPortrait(
  scene: Phaser.Scene,
  x: number,
  y: number,
  athlete: { skin: string; id?: string },
  displayHeight: number,
  equippedOverride?: Record<string, string>,
): AthleteView {
  const skin = getSkin(athlete.skin);
  // The shop passes an override to PREVIEW a not-yet-bought selection without touching the save.
  const equipped = equippedOverride ?? equippedFor(athlete);
  // Prefer the baked head-accessory variant; otherwise the plain hero sprite.
  const key = headVariantKey(scene, skin.id, equipped) ?? `${skin.id}_full`;
  if (scene.textures.exists(key)) return new MascotSprite(scene, x, y, key, displayHeight, { skinId: skin.id, equipped });
  return createAthleteView(scene, x, y, athlete, displayHeight);
}
